import { useRef } from 'react';
import { Form } from 'react-bootstrap';
import { isAndroid, isIOS } from 'react-device-detect';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useAppDispatch, useAppSelector } from '../../app/hook';
import PopoverWithAudio, { IPopoverWithAudioType } from '../../components/common/PopoverWithAudio';
import ProBadge from '../../components/ui/ProBadge';
import { AlertsType } from '../../constants/alerts.constant';
import usePermission from '../../hooks/usePermission';
import Utilities from '../../utils';
import AppearanceCustomPoweredBy from './AppearanceCustomPoweredBy';
import AppearanceDownTime from './AppearanceDownTime';
import AppearanceMessageNotificationComponent from './AppearanceMessageNotification.component';
import AppearanceNotificationComponent from './AppearanceNotification.component';
import { handleResetAppearance, handleUpdateAppearanceItem, selectAppearanceState } from './appearanceSlice';
import ColorDropDown from './ColorDropDown';
import IconCustomizationSection from './IconCustomizationSection';
import { IAiAppearance, IVideoScenarioAppearance } from './types';

const AppearanceVideoScenario = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const permissions = usePermission();
  const { configs, originalData } = useAppSelector(selectAppearanceState);

  const iconImageRef = useRef<HTMLInputElement | null>(null);
  const iconVideoRef = useRef<HTMLInputElement | null>(null);

  const SaveStatsData: IPopoverWithAudioType = {
    contentText: t('DashboardSettingCustomizeChat/pop-09'),
  };

  const {
    register,
    setValue,
    watch,
    formState: { errors },
    control,
  } = useFormContext<IAiAppearance>();

  // ========================= General ============================ //
  const handleUpdateItem = (key: keyof IAiAppearance, value: any) => {
    if (key === 'icon' && value.type === 'image' && value.value && typeof value.value === 'object') {
      const file: File = value.value;

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return;
      }

      if (file.size / (1024 * 1024) > 5) {
        toast.error(t(AlertsType.ISSTKO008, { size: 5 }));
        return;
      }

      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type)) {
        toast.error(t(AlertsType.ISSTKO009));
        return;
      }
    }

    if (key === 'icon' && value.type === 'video' && value.value && typeof value.value === 'object') {
      const file: File = value.value;

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return;
      }

      if (file.size / (1024 * 1024) > 30) {
        toast.error(t(AlertsType.ISSTKO008, { size: 30 }));
        return;
      }

      if (file?.type) {
        if (!Utilities.isSupportedVideoFormatMimeType(file.type)) {
          toast.error(t(AlertsType.ISSTKO009));
          return;
        }
      } else if (file?.name) {
        if (!Utilities.isSupportedVideoFormatWithFileName(file.name)) {
          toast.error(t(AlertsType.ISSTKO009));
          return;
        }
      }
    }

    setValue(key, value);
    dispatch(handleUpdateAppearanceItem({ key, value }));

    if (key === 'icon' && value.type !== 'video')
      dispatch(
        handleUpdateAppearanceItem({
          key: 'roundedVideo',
          value: (configs as IVideoScenarioAppearance).roundedVideo,
        })
      );
  };

  const alwaysShowButtonsPopData: IPopoverWithAudioType = {
    contentText: t('DashboardSettingCustomizeChat/pop-11'),
  };

  return (
    <div className="appearance-video-scenario tw-w-full">
      {/* Theme */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
        <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
          {t('DashboardSettingCustomizeChat/l-06')}
        </div>
        <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full">
          <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-w-full">
            <div className="tw-w-full tw-col-span-12">
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t('DashboardSettingCustomizeChat/l-12')}
                  {!permissions.settingsPage.widget.customization.theme && <ProBadge className="tw-ms-2" />}
                </Form.Label>
                <Form.Select
                  translate="no"
                  disabled={!permissions.settingsPage.widget.customization.theme}
                  isInvalid={!!errors.theme}
                  {...register('theme', {
                    onChange(event) {
                      dispatch(
                        handleUpdateAppearanceItem({
                          key: 'theme',
                          value: event.target.value,
                        })
                      );
                    },
                  })}
                >
                  <option value={'light'}>{t('DashboardSettingCustomizeChat/l-05')}</option>
                  <option value={'dark'}>{t('DashboardSettingCustomizeChat/l-07')}</option>
                </Form.Select>
                {errors.theme && <span className="tw-text-red-500 tw-text-sm tw-block">{errors.theme?.message}</span>}
              </Form.Group>
            </div>
            <div className="tw-w-full tw-col-span-12">
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t('DashboardSettingCustomizeChat/t-01')}
                  {!permissions.settingsPage.widget.customization.colors && <ProBadge className="tw-ms-2" />}
                </Form.Label>
                <Controller
                  control={control}
                  name="color"
                  render={({ field: { onChange, value } }) => (
                    <ColorDropDown
                      acceptableColors={[
                        { label: 'Grey', value: '#6B7280' },
                        { label: 'Red', value: '#EF4444' },
                        { label: 'Orange', value: '#F97316' },
                        { label: 'Yellow', value: '#F59E0B' },
                        { label: 'Green', value: '#10B981' },
                        { label: 'Teal', value: '#14B8A6' },
                        { label: 'Cyan', value: '#06B6D4' },
                        { label: 'Light Blue', value: '#0EA5E9' },
                        { label: 'Blue', value: '#3B82F6' },
                        { label: 'Indigo', value: '#6366F1' },
                        { label: 'Purple (Violet)', value: '#8B5CF6' },
                        { label: 'Pink', value: '#EC4899' },
                      ]}
                      disabled={!permissions.settingsPage.widget.customization.colors}
                      isInvalid={!!errors.color}
                      errorMessage={errors.color?.message}
                      onColorChanged={color => {
                        onChange(color);

                        dispatch(
                          handleUpdateAppearanceItem({
                            key: 'color',
                            value: color,
                          })
                        );
                      }}
                      currentColor={value}
                    />
                  )}
                />
              </Form.Group>
            </div>
            <div className="tw-w-full tw-col-span-12">
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t('DashboardSettingCustomizeChat/t-02')}
                  {!permissions.settingsPage.widget.customization.position && <ProBadge className="tw-ms-2" />}
                </Form.Label>
                <Form.Select
                  translate="no"
                  disabled={!permissions.settingsPage.widget.customization.position}
                  isInvalid={!!errors.position}
                  {...register('position', {
                    onChange(event) {
                      dispatch(
                        handleUpdateAppearanceItem({
                          key: 'position',
                          value: event.target.value,
                        })
                      );
                    },
                  })}
                >
                  <option value={'right'}>{t('DashboardSettingCustomizeChat/l-09')}</option>
                  <option value={'left'}>{t('DashboardSettingCustomizeChat/l-08')}</option>
                </Form.Select>
                {errors.position && <span className="tw-text-red-500 tw-text-sm tw-block">{errors.position?.message}</span>}
              </Form.Group>
            </div>
          </div>
        </div>
      </div>
      <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

      {/* Floating button */}
      <IconCustomizationSection
        permissions={permissions}
        configs={configs}
        originalData={originalData}
        iconImageRef={iconImageRef}
        iconVideoRef={iconVideoRef}
        isIOS={isIOS}
        isAndroid={isAndroid}
        errors={errors}
        dispatch={dispatch}
        handleResetAppearance={handleResetAppearance}
        handleUpdateItem={handleUpdateItem}
      />

      <hr className="tw-border-gray-200 tw-pb-2 ty-m-0" />
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-5 tw-flex">
        <Form.Group className="tw-w-full  tw-flex  tw-flex-col ">
          <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            {t('DashboardSettingCustomizeChat/l-19')}
            {!permissions.settingsPage.widget.customization.displayAfter && (
              <ProBadge
                className="tw-ms-2"
                onClose={() => {
                  dispatch(handleResetAppearance());
                }}
              />
            )}
          </Form.Label>

          <Form.Control
            isInvalid={!!errors.displayAfter}
            maxLength={30}
            max={300}
            disabled={!permissions.settingsPage.widget.customization.displayAfter}
            {...register('displayAfter', {
              onChange: event => {
                dispatch(
                  handleUpdateAppearanceItem({
                    key: 'displayAfter',
                    value: event.target.value != '' ? event.target.value : null,
                  })
                );
              },
            })}
            placeholder={'0'}
            type={'number'}
            className="tw-h-10 tw-w-32 tw-px-4 tw-py-2.5 tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-inline-flex"
          />
          <Form.Control.Feedback type="invalid" className="d-block font-poppins">
            {errors?.displayAfter?.message as string}
          </Form.Control.Feedback>
        </Form.Group>
      </div>

      <AppearanceNotificationComponent register={register} watch={watch} className="tw-mt-6" />

      <AppearanceMessageNotificationComponent register={register} watch={watch} errors={errors} control={control} />

      <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

      {/* Other */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
        <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
          {t('DashboardSettingCustomizeChat/other')}
        </div>
        <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
          <Form.Group className="tw-flex tw-items-center tw-gap-2">
            <Form.Check
              disabled={!permissions.settingsPage.widget.customization.alwaysShowButtons}
              type="switch"
              id="alwaysShowButtons"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t('DashboardSettingCustomizeChat/t-11')}
              isInvalid={!!errors.alwaysShowButtons}
              feedbackType="invalid"
              feedback={errors.alwaysShowButtons?.message}
              {...register('alwaysShowButtons', {
                onChange: e => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: 'alwaysShowButtons',
                      value: e.target.checked,
                    })
                  );
                },
              })}
            />
            {!permissions.settingsPage.widget.customization.alwaysShowButtons && <ProBadge className="tw-ms-2" />}
            <PopoverWithAudio
              data={{
                contentText: alwaysShowButtonsPopData.contentText,
              }}
            />
          </Form.Group>

          <AppearanceCustomPoweredBy
            hasPermission={permissions.settingsPage.widget.customization.poweredBy}
            isInvalid={!!errors.customPoweredBy}
            errors={errors.customPoweredBy}
            setFormValue={setValue}
            register={register}
            watch={watch}
          />

          <Form.Group className="tw-flex tw-items-center tw-gap-2">
            <Form.Check
              disabled={!permissions.settingsPage.widget.customization.defaultOpen}
              type="switch"
              id="isOpen"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t('DashboardSettingCustomizeChat/l-04')}
              isInvalid={!!errors.isOpen}
              feedbackType="invalid"
              feedback={errors.isOpen?.message}
              {...register('isOpen', {
                onChange: e => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: 'isOpen',
                      value: e.target.checked,
                    })
                  );
                },
              })}
            />
            {!permissions.settingsPage.widget.customization.defaultOpen && <ProBadge className="tw-ms-2" />}
          </Form.Group>

          {watch('isOpen') && (
            <Form.Group className="tw-flex tw-items-center tw-gap-2">
              <Form.Check
                disabled={!permissions.settingsPage.widget.customization.isFullScreen}
                type="switch"
                id="isFullScreen"
                className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t('DashboardSettingCustomizeChat/l-15')}
                isInvalid={!!errors.isFullScreen}
                feedbackType="invalid"
                feedback={errors.isFullScreen?.message}
                {...register('isFullScreen', {
                  onChange: e => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: 'isFullScreen',
                        value: e.target.checked,
                      })
                    );
                  },
                })}
              />
              {!permissions.settingsPage.widget.customization.isFullScreen && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
            </Form.Group>
          )}

          <Form.Group className="tw-flex tw-items-center tw-gap-2">
            <Form.Check
              type="switch"
              id="saveStats"
              disabled={!permissions.settingsPage.widget.customization.saveStats}
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t('DashboardSettingCustomizeChat/l-17')}
              isInvalid={!!errors.saveStats}
              feedbackType="invalid"
              feedback={errors.saveStats?.message}
              {...register('saveStats', {
                onChange: e => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: 'saveStats',
                      value: e.target.checked,
                    })
                  );
                },
              })}
            />
            {!permissions.settingsPage.widget.customization.saveStats && (
              <ProBadge
                className="tw-ms-2"
                onClose={() => {
                  dispatch(handleResetAppearance());
                }}
              />
            )}
            <PopoverWithAudio
              data={{
                contentText: SaveStatsData.contentText,
              }}
            />
          </Form.Group>
          <AppearanceDownTime errors={errors} register={register} watch={watch} />
        </div>
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(AppearanceVideoScenario);
